/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {MQWebSocket} from 'phenix-web-proto';
import * as _ from 'phenix-web-lodash-light';
import {ILogger} from 'services/logger/LoggerInterface';
import pcastProtocol from 'services/messaging/pcast.proto.json';
import analyticsProtocol from 'services/messaging/analytics.proto.json';
import {wsStatus} from 'store/action/websocket';

export const requestType = {
  authenticate: 'pcast.Authenticate',
  logout: 'pcast.Bye',
  timeToFirstFrame: 'analytics.GetTimeToFirstFrameCDF',
  geographicUsageStatistics: 'analytics.GetGeographicUsage',
  activeUsers: 'analytics.GetActiveUsers'
};

export type PhenixWebSocketType = PhenixWebSocket & MQWebSocket;

class PhenixWebSocket extends MQWebSocket {
  private _connectionStatus = '';
  private sessionId = '';
  private pendingRequestCount = 0;
  public uniqueId;

  constructor(...args: [
    host?: string,
    logger?: ILogger,
    protocols?: [
      pcastProtocol: typeof pcastProtocol,
      analyticsProtocol: typeof analyticsProtocol
    ]
  ]) {
    super(...args);
    this.uniqueId = _.uniqueId();
  }

  /**
   * Convert the inherited sendRequest method to an async function (promise).
   * @param request
   * @param data
   */
  async sendRequest(request: string, data: unknown): Promise<any> { // eslint-disable-line
    this.pendingRequestCount++;

    return new Promise((resolve, reject): void => {
      super.sendRequest(request, data, (err: string, response: unknown) => {
        if (err) {
          reject(err);
        } else {
          resolve(response);
        }

        this.pendingRequestCount--;
      });
    });
  }

  isAuthenticated(): boolean {
    return this._connectionStatus === wsStatus.authenticated;
  }

  isConnected(): boolean {
    return this._connectionStatus === wsStatus.connected || this._connectionStatus === wsStatus.authenticated;
  }

  setStatus(status: string): string {
    this._connectionStatus = status;

    return status;
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  async logout(reason = 'Event logout'): Promise<boolean> {
    if (!this.isAuthenticated()) {
      super.disconnect();

      return true;
    }

    const data = {
      sessionId: this.sessionId,
      reason
    };

    try {
      const response = await this.sendRequest(requestType.logout, data);

      if (response.status === 'ok') {
        super.disconnect();
        this.setStatus(wsStatus.disconnected);

        return true;
      }
    } catch (error) {
      console.error(error);
    }

    return false;
  }

  hasPendingRequest(): boolean {
    return this.pendingRequestCount > 0;
  }

  isDisconnected(): boolean {
    return this._connectionStatus === wsStatus.disconnected;
  }
}

export default PhenixWebSocket;